/**
 * Parser for QuickBooks Desktop "Customer Balance Detail" CSV exports.
 *
 * Two layouts in the wild:
 *   Compact (7 cols):  ,Type,Date,Num,Account,Amount,Balance
 *   Detailed (9 cols): ,Type,Date,Num,Memo,Account,Split,Amount,Balance
 *
 * The detailed export carries `Memo` (free-form description we use as the
 * charge label) and `Split` (the offsetting revenue/expense GL account —
 * e.g. "3060 · WATER & SEWER REVENUE" — which we translate into the charge
 * category). We auto-detect the layout from the header row.
 *
 * Amount sign convention from QuickBooks:
 *   - Invoice      → positive (increases AR)
 *   - Payment      → negative (reduces AR)
 *   - Credit Memo  → negative (reduces AR)
 * We strip the sign downstream since our schema stores absolute cents.
 */

export type TxnType = 'Invoice' | 'Payment' | 'Credit Memo';

export interface ParsedTxn {
	type: TxnType;
	/** QB "Trans #" — present only in the FULL report layout. The exact join
	 *  key back to the journal export's transactions. */
	transId?: string;
	/** M/D/YYYY or MM/DD/YYYY as it appears in the CSV. */
	dateStr: string;
	/** Epoch ms at local midnight — null if date couldn't be parsed. */
	dateMs: number | null;
	/** QB transaction number (invoice #, payment ref, etc.) — may be empty. */
	num?: string;
	/** QB account string, e.g. "1100 · ACCOUNTS RECEIVABLE/<PREPAIDS>". */
	account?: string;
	/** Free-form description from the QB Memo column (detailed exports only). */
	memo?: string;
	/** QB "Split" column — the offsetting GL account for this row. Used to
	 *  derive a charge category (e.g. "3060 · WATER & SEWER REVENUE" → Water). */
	split?: string;
	/** Humanized category derived from `split` — e.g. "Water", "Late Fee". */
	category?: string;
	/** Signed decimal amount from the CSV (e.g. 150.00 or -150.00). */
	amount: number;
	/** Absolute cents (always positive) — ready for our amount_cents columns. */
	amountCents: number;
}

export interface ParsedSection {
	customerName: string;
	transactions: ParsedTxn[];
	/** The "Total <customer>" row's amount, when present. */
	reportedTotal?: number;
}

export interface ParsedTransactionsCsv {
	sections: ParsedSection[];
	/** The "TOTAL" footer row amount. */
	grandTotal?: number;
	errors: string[];
}

/**
 * Tolerant CSV line splitter that handles double-quoted fields with embedded
 * commas. Not fully RFC 4180 — doesn't handle embedded newlines — but that's
 * fine for QB exports which never wrap fields.
 */
const splitCsvLine = (line: string): string[] => {
	const out: string[] = [];
	let cur = '';
	let inQuotes = false;
	for (let i = 0; i < line.length; i++) {
		const c = line[i];
		if (inQuotes) {
			if (c === '"') {
				if (line[i + 1] === '"') { cur += '"'; i++; }
				else { inQuotes = false; }
			} else {
				cur += c;
			}
		} else if (c === '"') {
			inQuotes = true;
		} else if (c === ',') {
			out.push(cur);
			cur = '';
		} else {
			cur += c;
		}
	}
	out.push(cur);
	return out;
};

const parseDate = (s: string): number | null => {
	if (!s) return null;
	const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
	if (!m) return null;
	const [, mm, dd, yy] = m;
	const year = yy.length === 2 ? 2000 + Number(yy) : Number(yy);
	// Business date — midnight UTC, not local midnight (which is off-grid and
	// lands on the previous day east of Greenwich).
	const ms = Date.UTC(year, Number(mm) - 1, Number(dd));
	return isFinite(ms) ? ms : null;
};

const parseAmount = (s: string): number | null => {
	if (s == null || s === '') return null;
	const n = Number(String(s).replace(/[,"]/g, ''));
	return isFinite(n) ? n : null;
};

// Map QB Split GL accounts → human-friendly charge category labels.
// Keys are the leading numeric account code; everything else uses the
// trimmed description (with "REVENUE"/"INCOME" suffixes stripped).
const SPLIT_CATEGORY_BY_CODE: Record<string, string> = {
	'3060': 'Water',
	'3110': 'Late Fee',
	'3120': 'Legal Fees',
	'3122': 'Legal Administrative',
	'3030': 'Special Assessment',
	'3000': 'Operating Income',
	'3140': 'Returned Payment Fee',
	'3185': 'SIUA',
	'7900': 'Write-Off',
};

const categoryFromSplit = (split: string | undefined, memo: string | undefined): string | undefined => {
	if (!split) return undefined;
	// "-SPLIT-" means a multi-line invoice; fall back to the memo so we keep
	// some signal (e.g. "Monthly Association Fee" → user can re-categorize).
	if (split === '-SPLIT-') return memo?.trim() || undefined;
	const m = split.match(/^(\d+)/);
	if (m && SPLIT_CATEGORY_BY_CODE[m[1]]) return SPLIT_CATEGORY_BY_CODE[m[1]];
	// Strip leading "NNNN · " and trailing " REVENUE"/" INCOME" so we surface
	// a clean label even for codes we didn't pre-map.
	return split
		.replace(/^\d+\s*[·•]\s*/, '')
		.replace(/\s+(REVENUE|INCOME)$/i, '')
		.trim() || undefined;
};

interface ColumnLayout {
	type: number;
	date: number;
	num: number;
	transId?: number;
	memo?: number;
	account: number;
	split?: number;
	amount: number;
}

/** Positional fallback for data rows that appear before any header. */
const COMPACT_LAYOUT: ColumnLayout = { type: 1, date: 2, num: 3, account: 4, amount: 5 };

/**
 * Map columns by header NAME, not position. Three layouts exist in the wild:
 *   Compact (7 cols):   ,Type,Date,Num,Account,Amount,Balance
 *   Detailed (9 cols):  ,Type,Date,Num,Memo,Account,Split,Amount,Balance
 *   Full (36+ cols):    ,Trans #,Type,Entered/Last Modified,…,Date,Num,…,Memo,
 *                       …,Account,Amount,Balance  (no Split column)
 * Hardcoding indexes silently mis-parsed the full layout (Type landed on the
 * Trans # column and every row errored "unknown type"), so resolve each named
 * column and fall back positionally only when a name is missing.
 */
const detectLayout = (headerCells: string[]): ColumnLayout => {
	const named = new Map<string, number>();
	headerCells.forEach((c, i) => {
		const key = c.trim();
		if (key && !named.has(key)) named.set(key, i);
	});
	const type = named.get('Type');
	const date = named.get('Date');
	const amount = named.get('Amount');
	if (type == null || date == null || amount == null) return COMPACT_LAYOUT;
	return {
		type,
		date,
		num: named.get('Num') ?? type + 2,
		transId: named.get('Trans #'),
		memo: named.get('Memo'),
		account: named.get('Account') ?? amount - 1,
		split: named.get('Split'),
		amount,
	};
};

export function parseTransactionsCsv(text: string): ParsedTransactionsCsv {
	const out: ParsedTransactionsCsv = { sections: [], errors: [] };
	const lines = text.split(/\r?\n/);
	let current: ParsedSection | null = null;
	let layout: ColumnLayout = COMPACT_LAYOUT;
	let layoutDetected = false;

	for (let i = 0; i < lines.length; i++) {
		const line = lines[i];
		if (!line.trim()) continue;
		const cells = splitCsvLine(line);
		const label = (cells[0] ?? '').trim();

		// Header row — "Type" sits at col 1 (compact/detailed) or col 2 (full,
		// behind Trans #).
		if (!layoutDetected && cells.some((c) => c.trim() === 'Type') && cells.some((c) => c.trim() === 'Amount')) {
			layout = detectLayout(cells);
			layoutDetected = true;
			continue;
		}
		const type = (cells[layout.type] ?? '').trim();

		// Grand total footer
		if (label === 'TOTAL') {
			out.grandTotal = parseAmount(cells[layout.amount]) ?? undefined;
			continue;
		}

		// Per-customer subtotal row
		if (label.startsWith('Total ')) {
			if (current) current.reportedTotal = parseAmount(cells[layout.amount]) ?? undefined;
			current = null;
			continue;
		}

		// Section header (label populated, no type)
		if (label && !type) {
			current = { customerName: label, transactions: [] };
			out.sections.push(current);
			continue;
		}

		// Transaction detail row
		if (!current) {
			out.errors.push(`Line ${i + 1}: transaction row outside any customer section`);
			continue;
		}
		if (type !== 'Invoice' && type !== 'Payment' && type !== 'Credit Memo') {
			out.errors.push(`Line ${i + 1}: unknown type "${type}"`);
			continue;
		}
		if (!layoutDetected) {
			out.errors.push(`Line ${i + 1}: data row before header — assuming compact layout`);
		}
		const dateStr = (cells[layout.date] ?? '').trim();
		const dateMs = parseDate(dateStr);
		if (!dateMs) {
			out.errors.push(`Line ${i + 1}: unparseable date "${dateStr}"`);
			continue;
		}
		const amount = parseAmount(cells[layout.amount]);
		if (amount == null) {
			out.errors.push(`Line ${i + 1}: missing amount`);
			continue;
		}
		const memo = layout.memo != null ? (cells[layout.memo] ?? '').trim() || undefined : undefined;
		const split = layout.split != null ? (cells[layout.split] ?? '').trim() || undefined : undefined;
		current.transactions.push({
			type,
			transId: (layout.transId != null ? (cells[layout.transId] ?? '').trim() : '') || undefined,
			dateStr,
			dateMs,
			num: (cells[layout.num] ?? '').trim() || undefined,
			account: (cells[layout.account] ?? '').trim() || undefined,
			memo,
			split,
			category: categoryFromSplit(split, memo),
			amount,
			amountCents: Math.round(Math.abs(amount) * 100),
		});
	}

	return out;
}
